/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Drawer, Empty, Form, InputNumber, Input, Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  useGetInventoryLotsQuery,
  useGetInventoryTransactionsQuery,
  useGetInventoryReservationsQuery,
  useWithdrawInventoryMutation,
  useReserveInventoryMutation,
  useCorrectInventoryMutation,
  useReleaseReservationMutation,
} from '../../api/inventoryApi';
import type { InventoryLot } from '../../types/inventory';

const { Text } = Typography;

const fmt = (v: string | null | undefined): string => {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${Number(n.toFixed(3))} кг` : '—';
};

const ORIGIN_LABELS: Record<string, string> = {
  harvest_fact: 'Сбор',
  imported: 'Импорт',
  admin_adjustment: 'Корр. админа',
  scenario_grant: 'Сценарий',
};

const TX_LABELS: Record<string, string> = {
  deposit: 'Поступление',
  withdraw_sale: 'Продажа',
  withdraw_gift: 'Дарение',
  withdraw_consumption: 'Потребление',
  withdraw_waste: 'Утрата',
  withdraw_scenario: 'Сценарий',
  reservation_hold: 'Резерв',
  reservation_release: 'Снятие резерва',
  correction_in: 'Коррекция +',
  correction_out: 'Коррекция −',
};

type ActionKind = 'withdraw' | 'reserve' | 'correct';

const DATE_FMT = 'YYYY-MM-DD';

// Складская операция датируется сим-днём в окне [дата сбора лота, курсор]
// (RFC_HARVEST_ECONOMY §7): раньше даты — лота ещё не было, позже курсора —
// откат курсора не сможет её снять.
const LEDGER_ERROR_TEXT: Record<string, string> = {
  LEDGER_DATE_REQUIRED: 'Укажите дату операции (сим-день)',
  LEDGER_DATE_OUT_OF_WINDOW: 'Дата вне окна: от даты сбора партии до текущего дня сейва',
  LEDGER_NOT_AVAILABLE: 'Склад доступен только в свободной игре',
};
const ledgerError = (e: any, fallback: string): string =>
  LEDGER_ERROR_TEXT[e?.data?.code as string] || e?.data?.message || fallback;

interface Props {
  saveId: string;
  /** Тип сейва: складские команды принимает только `free`. */
  saveType?: string | null;
  /** Курсор сейва (сим-дата YYYY-MM-DD) — дата операции по умолчанию и верхняя граница окна. */
  cursorDate?: string | null;
}

const InventoryLedgerSection: React.FC<Props> = ({ saveId, saveType = null, cursorDate = null }) => {
  const { data: lots = [], isLoading } = useGetInventoryLotsQuery({ saveId });
  const { data: openReservations = [] } = useGetInventoryReservationsQuery({ saveId, status: 'open' });
  const [withdraw, { isLoading: withdrawing }] = useWithdrawInventoryMutation();
  const [reserve, { isLoading: reserving }] = useReserveInventoryMutation();
  const [correct, { isLoading: correcting }] = useCorrectInventoryMutation();
  const [releaseReservation] = useReleaseReservationMutation();

  const ledgerWritable = saveType == null || saveType === 'free';
  const cursorDay = cursorDate ? dayjs(cursorDate) : null;

  const reservationsByLot = new Map<string, typeof openReservations>();
  for (const r of openReservations) {
    const list = reservationsByLot.get(r.lot_id) || [];
    list.push(r);
    reservationsByLot.set(r.lot_id, list);
  }

  const doRelease = async (reservationId: string, lotId: string) => {
    if (!cursorDay) {
      message.error(LEDGER_ERROR_TEXT.LEDGER_DATE_REQUIRED);
      return;
    }
    try {
      // Снятие резерва датируется курсором: это «сегодня» сейва, верхняя граница окна.
      await releaseReservation({ saveId, reservationId, lotId, payload: { effectiveDate: cursorDay.format(DATE_FMT) } }).unwrap();
      message.success('Резерв снят');
    } catch (e: any) {
      message.error(ledgerError(e, 'Ошибка снятия резерва'));
    }
  };

  const [action, setAction] = useState<{ kind: ActionKind; lot: InventoryLot } | null>(null);
  const [txLot, setTxLot] = useState<InventoryLot | null>(null);
  const [form] = Form.useForm();

  const openAction = (kind: ActionKind, lot: InventoryLot) => {
    form.resetFields();
    form.setFieldsValue({ effectiveDate: cursorDay });
    setAction({ kind, lot });
  };

  const disabledDateFor = (lot: InventoryLot) => (d: Dayjs) => {
    const origin = lot.origin_date ? dayjs(lot.origin_date) : null;
    if (origin && d.isBefore(origin, 'day')) return true;
    if (cursorDay && d.isAfter(cursorDay, 'day')) return true;
    return false;
  };

  const submit = async () => {
    if (!action) return;
    try {
      const v = await form.validateFields();
      const { kind, lot } = action;
      const effectiveDate = (v.effectiveDate as Dayjs).format(DATE_FMT);
      if (kind === 'withdraw') {
        await withdraw({ saveId, lotId: lot.id, payload: { quantity: v.quantity, reason: v.reason, effectiveDate, counterpartyRef: v.counterpartyRef, notes: v.notes } }).unwrap();
        message.success('Списано со склада');
      } else if (kind === 'reserve') {
        await reserve({ saveId, lotId: lot.id, payload: { quantity: v.quantity, effectiveDate, reason: v.reason } }).unwrap();
        message.success('Зарезервировано');
      } else {
        await correct({ saveId, lotId: lot.id, payload: { quantity: v.quantity, direction: v.direction, effectiveDate, reason: v.reason } }).unwrap();
        message.success('Коррекция применена');
      }
      setAction(null);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(ledgerError(e, 'Ошибка операции'));
    }
  };

  const columns = [
    {
      title: 'Продукт',
      key: 'product',
      render: (_: unknown, lot: InventoryLot) => (
        <Space direction="vertical" size={0}>
          <Text strong>{lot.produce_type_name || lot.crop_name || 'Продукция'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag>{ORIGIN_LABELS[lot.origin_type] || lot.origin_type}</Tag>{lot.origin_date}
          </Text>
        </Space>
      ),
    },
    { title: 'На складе', key: 'on_hand', align: 'right' as const, render: (_: unknown, l: InventoryLot) => fmt(l.quantity_on_hand) },
    { title: 'Резерв', key: 'reserved', align: 'right' as const, render: (_: unknown, l: InventoryLot) => fmt(l.quantity_reserved) },
    {
      title: 'Доступно', key: 'available', align: 'right' as const,
      render: (_: unknown, l: InventoryLot) => <Text strong>{fmt(l.quantity_available ?? l.quantity_on_hand)}</Text>,
    },
    {
      title: 'Действия', key: 'actions',
      render: (_: unknown, lot: InventoryLot) => {
        const resv = reservationsByLot.get(lot.id) || [];
        return (
          <Space direction="vertical" size={2}>
            <Space wrap size={4}>
              <Button size="small" type="primary" disabled={!ledgerWritable} onClick={() => openAction('withdraw', lot)}>Списать</Button>
              <Button size="small" disabled={!ledgerWritable} onClick={() => openAction('reserve', lot)}>Резерв</Button>
              <Button size="small" disabled={!ledgerWritable} onClick={() => openAction('correct', lot)}>Коррекция</Button>
              <Button size="small" type="link" onClick={() => setTxLot(lot)}>История</Button>
            </Space>
            {resv.length > 0 && (
              <Space wrap size={4}>
                {resv.map((r) => (
                  <Tag key={r.id} color="gold" closable={ledgerWritable} closeIcon="снять"
                    onClose={(e) => { e.preventDefault(); doRelease(r.id, lot.id); }}>
                    резерв {fmt(r.quantity)}
                  </Tag>
                ))}
              </Space>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card title="Готовая продукция (склад-ledger)" loading={isLoading}>
      {!ledgerWritable && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Складские операции доступны только в свободной игре: сценарный сейв показывает партии для чтения." />
      )}
      {lots.length === 0 ? (
        <Empty description="Нет материализованных партий. Они появляются при подтверждённом сборе урожая." />
      ) : (
        <Table
          rowKey="id"
          size="small"
          dataSource={lots}
          columns={columns}
          pagination={false}
        />
      )}

      <Modal
        open={!!action}
        title={action?.kind === 'withdraw' ? 'Списание со склада' : action?.kind === 'reserve' ? 'Резервирование' : 'Коррекция остатка'}
        onOk={submit}
        onCancel={() => setAction(null)}
        confirmLoading={withdrawing || reserving || correcting}
        okText={action?.kind === 'withdraw' ? 'Списать' : action?.kind === 'reserve' ? 'Зарезервировать' : 'Применить'}
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="effectiveDate"
            label="Дата операции (сим-день)"
            extra={action ? `Окно: с ${action.lot.origin_date} по ${cursorDay ? cursorDay.format('DD.MM.YYYY') : 'текущий день сейва'}` : undefined}
            rules={[{ required: true, message: LEDGER_ERROR_TEXT.LEDGER_DATE_REQUIRED }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              allowClear={false}
              disabledDate={action ? disabledDateFor(action.lot) : undefined}
              // Пикер якорится на курсор сейва, не на реальное «сегодня» (см. SaveDetailPage).
              defaultPickerValue={cursorDay ?? undefined}
            />
          </Form.Item>
          <Form.Item name="quantity" label="Количество, кг" rules={[{ required: true, message: 'Укажите количество' }, { type: 'number', min: 0.001, message: 'Больше нуля' }]}>
            <InputNumber min={0.001} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          {action?.kind === 'withdraw' && (
            <>
              <Form.Item name="reason" label="Причина" initialValue="sale" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'sale', label: 'Продажа' },
                  { value: 'gift', label: 'Дарение' },
                  { value: 'consumption', label: 'Потребление' },
                  { value: 'waste', label: 'Утрата/списание' },
                  { value: 'scenario', label: 'Сценарий' },
                ]} />
              </Form.Item>
              <Form.Item name="counterpartyRef" label="Контрагент (опц.)"><Input /></Form.Item>
              <Form.Item name="notes" label="Примечание (опц.)"><Input.TextArea rows={2} /></Form.Item>
            </>
          )}
          {action?.kind === 'reserve' && (
            <Form.Item name="reason" label="Причина (опц.)"><Input placeholder="контракт / бронь" /></Form.Item>
          )}
          {action?.kind === 'correct' && (
            <>
              <Form.Item name="direction" label="Направление" initialValue="out" rules={[{ required: true }]}>
                <Select options={[{ value: 'in', label: 'Добавить (+)' }, { value: 'out', label: 'Убавить (−)' }]} />
              </Form.Item>
              <Form.Item name="reason" label="Причина (опц.)"><Input /></Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Drawer
        open={!!txLot}
        width={520}
        title={`История партии · ${txLot?.produce_type_name || txLot?.crop_name || ''}`}
        onClose={() => setTxLot(null)}
        destroyOnClose
      >
        {txLot && <LotTransactions saveId={saveId} lotId={txLot.id} />}
      </Drawer>
    </Card>
  );
};

const LotTransactions: React.FC<{ saveId: string; lotId: string }> = ({ saveId, lotId }) => {
  const { data: txs = [], isLoading } = useGetInventoryTransactionsQuery({ saveId, lotId });
  return (
    <Table
      rowKey="id"
      size="small"
      loading={isLoading}
      dataSource={txs}
      pagination={false}
      columns={[
        { title: 'Дата', dataIndex: 'effective_date', key: 'date' },
        { title: 'Операция', key: 'type', render: (_: unknown, t) => <Tag>{TX_LABELS[t.transaction_type] || t.transaction_type}</Tag> },
        { title: 'Кол-во, кг', dataIndex: 'quantity', key: 'qty', align: 'right' as const, render: (q: string) => Number(Number(q).toFixed(3)) },
        { title: 'Причина', dataIndex: 'reason', key: 'reason', render: (r: string | null) => r || '—' },
      ]}
    />
  );
};

export default InventoryLedgerSection;
